import React, { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import ShowMoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { LinkItem, MenuGroup } from '@atlaskit/menu';

import { fg } from '@confluence/feature-gating';
import {
	DEFAULT_ADMIN_MANAGE_APPS,
	MARKETPLACE_APP_REQUESTS,
	ADMIN_APP_AUDIT_LOGS,
} from '@confluence/named-routes/entry-points/routes.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';

const useLazyClickMoreActionsAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ './analyticsCallbacks.ts'))
			.fireAppsMoreActionsButtonClicked,
);

const useLazyClickMoreActionsItemAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ './analyticsCallbacks.ts'))
			.fireAppsMoreActionsDropdownItemClicked,
);

export const AppsMoreActionsMenu = () => {
	const [isOpen, setIsOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireClickMoreActionsAnalytics = useLazyClickMoreActionsAnalytics(createAnalyticsEvent);

	const onClick = () => {
		setIsOpen((v) => !v);
		void fireClickMoreActionsAnalytics();
	};

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			content={() => <AppsMoreActionsMenuContent />}
			trigger={(triggerProps) => (
				<IconButton
					icon={(iconProps) => <ShowMoreIcon {...iconProps} size="small" />}
					label={<FormattedMessage {...i18n.moreActions} />}
					onClick={onClick}
					spacing="compact"
					appearance="subtle"
					{...triggerProps}
				/>
			)}
			placement="bottom-start"
			shouldRenderToParent
		/>
	);
};

const AppsMoreActionsMenuContent = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { environment, cloudId } = useSessionData();

	const fireClickMoreActionsItemManageAppsAnalytics = useLazyClickMoreActionsItemAnalytics(
		createAnalyticsEvent,
		'manageApps',
	);
	const fireClickMoreActionsItemAppAuditLogsAnalytics = useLazyClickMoreActionsItemAnalytics(
		createAnalyticsEvent,
		'appAuditLogs',
	);
	const fireClickMoreActionsItemViewAppRequestsAnalytics = useLazyClickMoreActionsItemAnalytics(
		createAnalyticsEvent,
		'viewAppRequests',
	);

	const adminHubBaseUrl =
		environment == 'PRODUCTION' ? 'https://admin.atlassian.com' : 'https://admin.stg.atlassian.com';
	const adminHubAppRequestsUrl = `${adminHubBaseUrl}/s/${cloudId}/user-connected-apps/tab/requested`;

	return (
		<MenuGroup>
			<LinkItem
				href={DEFAULT_ADMIN_MANAGE_APPS.toUrl()}
				target="_blank"
				onClick={fireClickMoreActionsItemManageAppsAnalytics}
			>
				<FormattedMessage {...i18n.manageApps} />
			</LinkItem>
			{fg('show_app_audit_logs_upm_dep') && (
				<LinkItem
					href={ADMIN_APP_AUDIT_LOGS.toUrl()}
					target="_blank"
					onClick={fireClickMoreActionsItemAppAuditLogsAnalytics}
				>
					<FormattedMessage {...i18n.appAuditLogs} />
				</LinkItem>
			)}
			{fg('confluence_frontend_enduser-emcee-discover') && (
				<LinkItem
					href={
						fg('should_redirect_to_admin-hub_for_app_requests')
							? adminHubAppRequestsUrl
							: MARKETPLACE_APP_REQUESTS.toUrl()
					}
					target="_blank"
					onClick={fireClickMoreActionsItemViewAppRequestsAnalytics}
				>
					<FormattedMessage {...i18n.viewAppRequests} />
				</LinkItem>
			)}
		</MenuGroup>
	);
};

const i18n = defineMessages({
	moreActions: {
		id: 'side-navigation.global.apps.more-actions',
		defaultMessage: 'More actions',
		description: 'Accessible label text for the ... button in the global navigation apps menu',
	},
	manageApps: {
		id: 'side-navigation.global.apps.manage-apps',
		defaultMessage: 'Manage apps',
		description: 'Manage apps link in the global navigation apps menu',
	},
	appAuditLogs: {
		id: 'side-navigation.global.apps.app-audit-log',
		defaultMessage: 'App audit logs',
		description: 'App audit logs link in the global navigation apps menu',
	},
	viewAppRequests: {
		id: 'side-navigation.global.apps.view-app-requests',
		defaultMessage: 'View app requests',
		description: 'View app requests link in the global navigation apps menu',
	},
});
